import React from "react";
import { LegalPageLayout, LegalSection } from "@/pages/LegalPage";

/**
 * /terms-of-service — Syarat & Ketentuan (Bahasa Indonesia). Same layout &
 * typography as PrivacyPolicy so both legal pages read as one document set.
 */
export default function TermsOfService() {
  return (
    <LegalPageLayout
      title="Syarat & Ketentuan"
      subtitle="Dengan menggunakan The School Reports, Anda menyetujui aturan berikut. Kami menulisnya sesingkat mungkin agar mudah dibaca — tanpa mengurangi kejelasan hukumnya."
      lastUpdated="13 Agustus 2026"
      testid="terms-of-service-page"
      seoDesc="Syarat & Ketentuan The School Reports — aturan penggunaan platform, larangan konten, dan batasan tanggung jawab."
    >
      <LegalSection heading="1. Definisi" testid="tos-sec-def">
        <p>
          <strong>"TSR"</strong> merujuk pada platform The School Reports beserta subdomain dan aplikasi turunannya. <strong>"Pengguna"</strong> adalah setiap orang yang mengakses, mendaftar, atau memposting konten di TSR. <strong>"Konten"</strong> mencakup review sekolah, teks forum, foto, komentar, dan materi lain yang diunggah Pengguna.
        </p>
      </LegalSection>

      <LegalSection heading="2. Aturan Umum Penggunaan Platform" testid="tos-sec-rules">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Anda harus berusia <strong>minimal 13 tahun</strong> untuk membuat akun. Orang tua bertanggung jawab atas akun yang dibuat untuk anak di bawah umur.</li>
          <li>Satu orang, satu akun. Membuat akun ganda untuk mengelabui rating atau memanipulasi diskusi dilarang keras.</li>
          <li>Anda bertanggung jawab menjaga kerahasiaan password akun Anda. Semua aktivitas dari akun Anda dianggap dilakukan oleh Anda.</li>
          <li>TSR bebas biaya untuk pengguna umum. Fitur premium untuk sekolah dan mitra editorial diatur oleh perjanjian terpisah.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="3. Konten yang Dilarang" testid="tos-sec-prohibited">
        <p>Anda <strong>dilarang</strong> memposting konten berikut. Pelanggaran dapat berujung pada penghapusan konten, penonaktifan akun sementara, hingga blokir permanen tanpa pengembalian data:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li><strong>Review palsu / bayaran</strong> — menulis review tentang sekolah tanpa pengalaman langsung sebagai orang tua, siswa, atau alumni.</li>
          <li><strong>Spam</strong> — teks berulang, promosi produk/jasa yang tidak relevan, link phishing atau afiliasi tersembunyi.</li>
          <li><strong>Ujaran kebencian & SARA</strong> — konten yang menyerang seseorang atau kelompok berdasarkan suku, agama, ras, gender, orientasi seksual, atau disabilitas.</li>
          <li><strong>Doxing & pelecehan</strong> — membagikan data pribadi orang lain (nomor HP, alamat rumah, foto anak yang bukan milik Anda) tanpa izin.</li>
          <li><strong>Fitnah</strong> — tuduhan yang tidak berdasar terhadap sekolah, guru, atau siswa spesifik. Review kritis diperbolehkan asalkan berbasis pengalaman nyata.</li>
          <li><strong>Konten ilegal</strong> — pornografi, kekerasan grafis, perdagangan barang terlarang, atau pelanggaran hak cipta.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="4. Sifat Review di TSR" testid="tos-sec-opinion">
        <p>
          <strong>Review yang tayang di TSR adalah opini pribadi masing-masing Pengguna, bukan pernyataan resmi atau endorsement dari tim redaksi The School Reports.</strong> TSR memfasilitasi diskusi orang tua — kami tidak memverifikasi setiap klaim faktual di dalam review, meski kami dapat menandai review yang lolos proses moderasi tambahan.
        </p>
        <p>
          Sekolah yang merasa dirugikan oleh review tertentu dapat mengirim hak jawab lewat form <em>"Verifikasi Sekolah"</em> — TSR akan menampilkan hak jawab tersebut secara berdampingan dengan review asli, sesuai prinsip keberimbangan.
        </p>
      </LegalSection>

      <LegalSection heading="5. Hak TSR untuk Memoderasi Konten" testid="tos-sec-moderation">
        <p>TSR berhak — namun tidak berkewajiban — untuk:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Menyunting, menyembunyikan, atau menghapus konten yang melanggar aturan di bagian 3.</li>
          <li>Menonaktifkan akun sementara (hingga 30 hari) atau permanen bila terdapat pelanggaran berulang.</li>
          <li>Menerbitkan versi ringkas / AI summary dari review yang Anda tulis.</li>
          <li>Menutup atau memindahkan thread forum yang menyimpang dari topik.</li>
        </ul>
        <p>Keputusan moderasi diambil oleh tim editorial + relawan moderator TSR. Anda dapat mengajukan banding lewat email dan setiap banding ditinjau secara manual dalam 7 hari kerja.</p>
      </LegalSection>

      <LegalSection heading="6. Hak Kekayaan Intelektual" testid="tos-sec-ip">
        <p>
          Anda tetap memegang hak cipta atas konten (teks & foto) yang Anda posting. Namun dengan mempostingnya, Anda memberikan TSR lisensi non-eksklusif, bebas royalti, dan berlaku selama Anda mempertahankan akun untuk menampilkan, menyimpan, dan mendistribusikan konten tersebut sebagai bagian dari layanan TSR (termasuk dalam ringkasan editorial, indeks pencarian, dan cuplikan media sosial resmi).
        </p>
        <p>
          Logo, wordmark, dan desain visual TSR adalah milik The School Reports. Dilarang menggunakannya tanpa izin tertulis.
        </p>
      </LegalSection>

      <LegalSection heading="7. Batasan Tanggung Jawab" testid="tos-sec-liability">
        <p>
          TSR menyediakan platform "sebagaimana adanya". Kami berupaya menjaga keakuratan direktori sekolah dan integritas review, tetapi <strong>tidak menjamin</strong>:
        </p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Bahwa seluruh informasi sekolah (biaya, akreditasi, kurikulum) selalu terkini — silakan verifikasi langsung ke sekolah sebelum mengambil keputusan finansial.</li>
          <li>Bahwa platform akan bebas downtime, error, atau kehilangan data akibat gangguan pihak ketiga.</li>
          <li>Bahwa setiap review mencerminkan pengalaman yang akan Anda alami di sekolah yang sama.</li>
        </ul>
        <p>
          Dalam batas maksimum yang diizinkan hukum Republik Indonesia, TSR beserta pengurusnya tidak bertanggung jawab atas kerugian tidak langsung, kehilangan pendapatan, atau kerugian data yang timbul dari penggunaan platform.
        </p>
      </LegalSection>

      <LegalSection heading="8. Perubahan Syarat" testid="tos-sec-changes">
        <p>Kami dapat mengubah Syarat & Ketentuan ini seiring perkembangan platform. Perubahan material akan diumumkan minimal 14 hari sebelum berlaku efektif. Melanjutkan menggunakan TSR setelah tanggal efektif dianggap sebagai persetujuan atas versi baru.</p>
      </LegalSection>

      <LegalSection heading="9. Hukum yang Berlaku & Sengketa" testid="tos-sec-law">
        <p>
          Syarat ini tunduk pada hukum Republik Indonesia. Sengketa yang timbul akan diselesaikan secara musyawarah terlebih dahulu. Bila tidak tercapai kesepakatan, sengketa diselesaikan di Pengadilan Negeri Jakarta Selatan sesuai kompetensi.
        </p>
      </LegalSection>

      <LegalSection heading="10. Kontak" testid="tos-sec-contact">
        <p>Pertanyaan tentang Syarat & Ketentuan? Kirim ke{" "}
          <a href="mailto:hello@theschoolreports.id" className="text-[#D2673E] hover:underline">
            hello@theschoolreports.id
          </a>.
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}
